import type { ContractOverview, ContractSigningPage, CurrentSession } from '@elite-residence/api-client';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, type PointerEvent, useEffect, useRef, useState } from 'react';
import { useApiClient } from '../api/ApiClientProvider';
import { businessDate } from '../dates';
import { useLocale } from '../i18n/LocaleProvider';
import { Alert, Field, Loading, useAction } from '../ui/ui';

// Text and legal clauses are rendered as React text nodes, never executable
// HTML. The document always comes from the server's issued version.
type Both = { ar?: string; en?: string };
type Person = {
  fullName: string;
  nationality?: Both;
  identity?: { masked: string; typeLabel: Both; issuerLabel: Both };
};
type Content = {
  reference?: string;
  terms: { title?: Both; party?: Both; sections?: Both[] };
  booking: {
    reference: string;
    branch: string;
    unit: string;
    unitType?: Both;
    startDate: string;
    endDate: string;
    durationDays?: number;
    agreedTotal: string;
    deposit: { kind: string; amount?: string };
  };
  customer: {
    type: string;
    person?: Person;
    occupant?: Person;
    companyName?: string;
    phone?: string;
    email?: string;
    vatNumber?: string;
    representative?: { name: string; phone?: string; email?: string; roleTitle?: string };
  };
};
const Bilingual = ({ text }: { readonly text: Both | undefined }) => (
  <div className="er-contract-bilingual">
    <p lang="ar" dir="rtl">
      {text?.ar}
    </p>
    <p lang="en" dir="ltr">
      {text?.en}
    </p>
  </div>
);
function ContractDocument({ json }: { readonly json: string }) {
  const { locale } = useLocale();
  const ar = locale === 'ar';
  const content = JSON.parse(json) as Content;
  const b = content.booking;
  const c = content.customer;
  const person = c.person ?? c.occupant;
  return (
    <article className="er-contract-document">
      <header className="er-contract-heading">
        <p className="er-contract-brand">ELITE RESIDENCE · إليت ريزيدنس</p>
        <Bilingual text={content.terms.title} />
        <p>
          {ar ? 'مرجع العقد' : 'Contract reference'}: <bdi>{content.reference ?? `CTR-${b.reference}`}</bdi>
        </p>
      </header>
      <Bilingual text={content.terms.party} />
      <h2>{ar ? 'بيانات الإيجار والمستأجر' : 'Lease and tenant details'}</h2>
      <dl>
        <dt>{ar ? 'الحجز' : 'Booking'}</dt>
        <dd>{b.reference}</dd>
        <dt>{ar ? 'الفرع / الوحدة' : 'Branch / Unit'}</dt>
        <dd>
          {b.branch} / {b.unit}
        </dd>
        {b.unitType && (
          <>
            <dt>{ar ? 'نوع الوحدة' : 'Unit type'}</dt>
            <dd>
              {b.unitType.ar} / {b.unitType.en}
            </dd>
          </>
        )}
        <dt>{ar ? 'المدة (النهاية تاريخ المغادرة)' : 'Dates (end is departure)'}</dt>
        <dd dir="ltr">
          {b.startDate} → {b.endDate}
        </dd>
        {b.durationDays !== undefined && (
          <>
            <dt>{ar ? 'المدة بالأيام' : 'Duration in days'}</dt>
            <dd>{b.durationDays}</dd>
          </>
        )}
        <dt>{ar ? 'الإجمالي المتفق عليه' : 'Agreed total'}</dt>
        <dd dir="ltr">{b.agreedTotal} SAR</dd>
        <dt>{ar ? 'التأمين' : 'Deposit'}</dt>
        <dd>{b.deposit.kind === 'none' ? (ar ? 'لا يوجد تأمين' : 'No Deposit') : `${b.deposit.amount} SAR`}</dd>
        {c.companyName && (
          <>
            <dt>{ar ? 'الشركة' : 'Company'}</dt>
            <dd>{c.companyName}</dd>
          </>
        )}
        {c.vatNumber && (
          <>
            <dt>{ar ? 'الرقم الضريبي' : 'VAT number'}</dt>
            <dd>{c.vatNumber}</dd>
          </>
        )}
        {c.representative && (
          <>
            <dt>{ar ? 'الممثل' : 'Representative'}</dt>
            <dd>
              {c.representative.name} {c.representative.roleTitle} {c.representative.phone} {c.representative.email}
            </dd>
          </>
        )}
        {person && (
          <>
            <dt>{ar ? 'المستأجر / الشاغل' : 'Tenant / Occupant'}</dt>
            <dd>{person.fullName}</dd>
            <dt>{ar ? 'الجنسية' : 'Nationality'}</dt>
            <dd>
              {person.nationality?.ar} / {person.nationality?.en}
            </dd>
            <dt>{ar ? 'الهوية' : 'Identity'}</dt>
            <dd>
              {person.identity?.typeLabel.ar} / {person.identity?.typeLabel.en} · {person.identity?.issuerLabel.ar} /{' '}
              {person.identity?.issuerLabel.en} · {person.identity?.masked}
            </dd>
          </>
        )}
        {c.phone && (
          <>
            <dt>{ar ? 'الهاتف' : 'Phone'}</dt>
            <dd dir="ltr">{c.phone}</dd>
          </>
        )}
        {c.email && (
          <>
            <dt>{ar ? 'البريد' : 'Email'}</dt>
            <dd dir="ltr">{c.email}</dd>
          </>
        )}
      </dl>
      <p className="er-hint">
        {ar
          ? 'المبالغ المذكورة التزامات تعاقدية متفق عليها. هذا العقد ليس إثباتاً لاستلام دفعة.'
          : 'These amounts are agreed contractual obligations. This contract is not proof of payment.'}
      </p>
      <h2>{ar ? 'الشروط والأحكام' : 'Terms and conditions'}</h2>
      {content.terms.sections?.map((section) => (
        <Bilingual key={`${section.ar}-${section.en}`} text={section} />
      ))}
    </article>
  );
}

const gapLabels: Record<string, Both> = {
  booking_not_reserved: { ar: 'الحجز ليس في حالة محجوز', en: 'Booking must be Reserved' },
  guests: { ar: 'عدد النزلاء', en: 'Number of guests' },
  nationality: { ar: 'الجنسية', en: 'Nationality' },
  identity: { ar: 'الهوية', en: 'Identity' },
  customer_phone: { ar: 'هاتف العميل', en: 'Customer phone' },
  customer_email: { ar: 'بريد العميل', en: 'Customer email' },
  representative: { ar: 'ممثل الشركة', en: 'Company representative' },
  company_phone: { ar: 'هاتف الشركة أو الممثل', en: 'Company or representative phone' },
  occupant: { ar: 'الشاغل', en: 'Occupant' },
  occupant_nationality: { ar: 'جنسية الشاغل', en: 'Occupant nationality' },
  occupant_identity: { ar: 'هوية الشاغل', en: 'Occupant identity' },
  template: { ar: 'نموذج عقد معتمد وبيانات الطرف المؤجر', en: 'Approved contract template and contracting party' },
};

export function BookingContractPanel({
  bookingId,
  session,
}: {
  readonly bookingId: string;
  readonly session: CurrentSession;
}) {
  const client = useApiClient();
  const { locale } = useLocale();
  const ar = locale === 'ar';
  const can = (key: string) => session.permissions.includes(key);
  const action = useAction();
  const [preview, setPreview] = useState<string | null>(null);
  const [recipient, setRecipient] = useState({ name: '', email: '', reason: '' });
  const [evidence, setEvidence] = useState<{ ip: string; userAgent: string | null; signedAt: string } | null>(null);
  const overview = useQuery({
    queryKey: ['contract', bookingId],
    enabled: can('contract.view'),
    refetchInterval: (query) =>
      query.state.status === 'success' && query.state.data?.versions.some((v) => v.signedAt && !v.pdf && !v.pdfFailed)
        ? 2000
        : false,
    queryFn: async () => {
      const r = await client.getBookingContract({ path: { bookingId } });
      if (r.status !== 200) throw new Error('contract_unavailable');
      return r.body;
    },
  });
  const refresh = () => overview.refetch();
  const apply = async (run: Parameters<typeof action.run>[0]) => {
    const result = await action.run(run);
    if (result) {
      setPreview(null);
      await refresh();
    }
  };
  const path = { bookingId };
  if (!session.permissions.some((key) => key.startsWith('contract.'))) return null;
  const version = overview.data?.versions.at(-1);
  const gaps = overview.data?.gaps ?? [];
  const day = (instant: string | null | undefined) => (instant ? businessDate(new Date(instant)) : null);
  const steps: { key: string; label: string; at: string | null }[] = [
    { key: 'issued', label: ar ? 'أُصدر' : 'Issued', at: day(version?.issuedAt) },
    { key: 'sent', label: ar ? 'أُرسل' : 'Sent', at: day(version?.sentAt) },
    { key: 'viewed', label: ar ? 'تمت المشاهدة' : 'Viewed', at: day(version?.viewedAt) },
    { key: 'signed', label: ar ? 'موقّع' : 'Signed', at: day(version?.signedAt) },
  ];
  const reached = steps.reduce((last, step, index) => (step.at ? index : last), -1);
  return (
    <section className="er-panel er-section-block" aria-label={ar ? 'العقد' : 'Contract'}>
      <div className="er-panel__head">
        <h2 className="er-panel__title">{ar ? 'العقد' : 'Contract'}</h2>
        <span className="er-spacer" />
        {version?.signedAt ? (
          <span className="er-chip er-chip--success">{ar ? 'موقّع' : 'Signed'}</span>
        ) : version ? (
          <span className="er-chip er-chip--info">{ar ? 'غير موقّع' : 'Not signed'}</span>
        ) : (
          <span className="er-chip">{ar ? 'لم يُصدر بعد' : 'Not issued yet'}</span>
        )}
      </div>
      <div className="er-panel__body er-fingrid">
        <div className="er-fingrid__main">
          <div className="er-steps" aria-hidden="true">
            {steps.map((step, index) => (
              <div
                key={step.key}
                className={`er-step${index <= reached ? ' is-done' : ''}${index === reached + 1 && reached >= 0 ? ' is-current' : ''}`}
              >
                {step.label}
                <span className="er-step__sub">{step.at ?? '–'}</span>
              </div>
            ))}
          </div>
          {version?.signedAt ? (
            <div className="er-callout er-callout--ok">
              {ar
                ? 'العقد الموقّع سجل تاريخي ثابت. لا يُعدَّل ولا يُستبدل.'
                : 'The signed contract is a fixed historical record. It is never edited or replaced.'}
            </div>
          ) : null}
          {overview.isError && <Alert kind="error">{ar ? 'تعذر تحميل العقد.' : 'Could not load the contract.'}</Alert>}
          {action.error && <Alert kind="error">{action.error}</Alert>}
          {gaps.length > 0 && !version?.signedAt && (
            <ul>
              {gaps.map((gap) => (
                <li key={gap}>{gapLabels[gap]?.[locale]}</li>
              ))}
            </ul>
          )}
          {can('contract.prepare') && (
            <button
              className="er-button secondary"
              type="button"
              disabled={action.busy}
              onClick={async () => {
                const r = await action.run(() => client.previewBookingContract({ path }));
                if (r?.status === 200) setPreview(r.body.contentJson);
              }}
            >
              {ar ? 'مراجعة المحتوى قبل الإصدار' : 'Review content before issue'}
            </button>
          )}
          {preview && <ContractDocument json={preview} />}
          {!version?.signedAt && can(version ? 'contract.reissue' : 'contract.issue') && (
            <button
              className="er-button"
              type="button"
              disabled={action.busy || gaps.length > 0}
              onClick={() =>
                void apply((key) =>
                  version
                    ? client.reissueBookingContract({ path, headers: { 'idempotency-key': key }, body: {} })
                    : client.issueBookingContract({ path, headers: { 'idempotency-key': key }, body: {} }),
                )
              }
            >
              {version
                ? ar
                  ? 'إصدار نسخة بديلة بعد المراجعة'
                  : 'Issue replacement after review'
                : ar
                  ? 'إصدار العقد'
                  : 'Issue contract'}
            </button>
          )}
          {version && !version.signedAt && version.signingEligible && (
            <>
              <Field id="contract-recipient-name" label={ar ? 'اسم المستلم' : 'Recipient name'}>
                <input
                  id="contract-recipient-name"
                  value={recipient.name}
                  onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                />
              </Field>
              <Field id="contract-recipient-email" label={ar ? 'بريد المستلم' : 'Recipient email'}>
                <input
                  id="contract-recipient-email"
                  type="email"
                  dir="ltr"
                  value={recipient.email}
                  onChange={(e) => setRecipient({ ...recipient, email: e.target.value })}
                />
              </Field>
              <Field id="contract-reason" label={ar ? 'سبب التصحيح أو السحب' : 'Reason for correction or withdrawal'}>
                <input
                  id="contract-reason"
                  value={recipient.reason}
                  onChange={(e) => setRecipient({ ...recipient, reason: e.target.value })}
                />
              </Field>
              {!version.sentAt && can('contract.send') && (
                <button
                  className="er-button"
                  type="button"
                  disabled={action.busy || !recipient.name || !recipient.email}
                  onClick={() =>
                    void apply((key) =>
                      client.sendContractLink({
                        path: { versionId: version.id },
                        headers: { 'idempotency-key': key },
                        body: { recipientName: recipient.name, recipientEmail: recipient.email },
                      }),
                    )
                  }
                >
                  {ar ? 'إرسال رابط التوقيع بالبريد' : 'Email signing link'}
                </button>
              )}
              {version.sentAt && can('contract.resend') && (
                <button
                  className="er-button"
                  type="button"
                  disabled={action.busy}
                  onClick={() =>
                    void apply((key) =>
                      client.resendContractLink({
                        path: { versionId: version.id },
                        headers: { 'idempotency-key': key },
                        body: {},
                      }),
                    )
                  }
                >
                  {ar ? 'إعادة الإرسال وإلغاء الروابط السابقة' : 'Resend and revoke earlier links'}
                </button>
              )}
              {version.sentAt && can('contract.change_recipient') && (
                <button
                  className="er-button secondary"
                  type="button"
                  disabled={action.busy || !recipient.reason || !recipient.email || !recipient.name}
                  onClick={() =>
                    void apply((key) =>
                      client.changeContractRecipient({
                        path: { versionId: version.id },
                        headers: { 'idempotency-key': key },
                        body: {
                          recipientName: recipient.name,
                          recipientEmail: recipient.email,
                          reason: recipient.reason,
                        },
                      }),
                    )
                  }
                >
                  {ar ? 'تصحيح بريد الشخص نفسه' : 'Correct email for the same person'}
                </button>
              )}
              {can('contract.withdraw_eligibility') && (
                <button
                  className="er-button secondary"
                  type="button"
                  disabled={action.busy || !recipient.reason}
                  onClick={() =>
                    void apply((key) =>
                      client.withdrawContractEligibility({
                        path: { versionId: version.id },
                        headers: { 'idempotency-key': key },
                        body: { reason: recipient.reason },
                      }),
                    )
                  }
                >
                  {ar ? 'سحب أهلية التوقيع' : 'Withdraw signing eligibility'}
                </button>
              )}
            </>
          )}
        </div>
        <aside className="er-fingrid__side">
          <h3 className="er-subhead">{ar ? 'النسخ' : 'Versions'}</h3>
          {(overview.data?.versions ?? []).map((v) => (
            <VersionStatus key={v.id} version={v} />
          ))}
          {version?.signedAt && version.pdf && can('contract.view') && (
            <button
              className="er-button"
              type="button"
              disabled={action.busy}
              onClick={async () => {
                const r = await action.run(() =>
                  client.downloadSignedContract({ path: { versionId: version.pdf?.versionId ?? '' } }),
                );
                if (r?.status === 200) {
                  const url = URL.createObjectURL(r.body);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'signed-contract.pdf';
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                }
              }}
            >
              {ar ? 'تنزيل العقد الموقع' : 'Download signed contract'}
            </button>
          )}
          {version?.signatureId && can('contract.technical_evidence_view') && (
            <button
              className="er-button secondary"
              type="button"
              onClick={async () => {
                const r = await action.run(() =>
                  client.getContractSigningEvidence({ path: { signatureId: version.signatureId ?? '' } }),
                );
                if (r?.status === 200) setEvidence(r.body);
              }}
            >
              {ar ? 'عرض أدلة التوقيع التقنية' : 'View technical signing evidence'}
            </button>
          )}
          {evidence && (
            <p dir="ltr">
              {evidence.ip} · {evidence.userAgent} · {evidence.signedAt}
            </p>
          )}
        </aside>
      </div>
    </section>
  );
}
function VersionStatus({ version: v }: { readonly version: ContractOverview['versions'][number] }) {
  const { locale } = useLocale();
  const ar = locale === 'ar';
  return (
    <p>
      {ar ? 'النسخة' : 'Version'} {v.versionNumber} ·{' '}
      {v.signedAt
        ? ar
          ? 'موقع'
          : 'Signed'
        : v.signingEligible
          ? ar
            ? 'صالح للتوقيع'
            : 'Eligible for signing'
          : ar
            ? 'غير صالح للتوقيع'
            : 'Invalidated'}
      {v.sentAt && <> · {ar ? 'أُرسل الرابط' : 'Link requested'}</>}
      {v.viewedAt && <> · {ar ? 'تمت المشاهدة' : 'Viewed'}</>}
      {v.signedAt && <> · {v.pdf ? (ar ? 'الملف جاهز' : 'PDF ready') : ar ? 'الملف قيد المعالجة' : 'PDF processing'}</>}
    </p>
  );
}

export function SigningPage() {
  const client = useApiClient();
  const { locale } = useLocale();
  const ar = locale === 'ar';
  const action = useAction();
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '');
  const [page, setPage] = useState<ContractSigningPage | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [declared, setDeclared] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const [reference, setReference] = useState('');
  const canvas = useRef<HTMLCanvasElement>(null);
  const previous = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
    let active = true;
    void client
      .openContractSigningPage({ body: { token } })
      .then((r) => {
        if (active) {
          if (r.status === 200) setPage(r.body);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [client, token]);
  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * 700) / r.width, y: ((e.clientY - r.top) * 220) / r.height };
  };
  const stroke = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!previous.current || action.busy) return;
    const next = point(e);
    const ctx = canvas.current?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(previous.current.x, previous.current.y);
      ctx.lineTo(next.x, next.y);
      ctx.strokeStyle = '#111';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.stroke();
      setDrawn(true);
    }
    previous.current = next;
  };
  const clear = () => {
    canvas.current?.getContext('2d')?.clearRect(0, 0, 700, 220);
    previous.current = null;
    setDrawn(false);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canvas.current || !drawn || !page || !accepted || (page.isCompany && !declared)) return;
    const r = await action.run(() =>
      client.submitContractSignature({
        body: {
          token,
          typedName: name,
          accepted,
          companyDeclarationAccepted: declared,
          ...(title ? { roleTitle: title } : {}),
          signaturePngBase64: canvas.current?.toDataURL('image/png').split(',')[1] ?? '',
        },
      }),
    );
    if (r?.status === 200) {
      setReference(r.body.signingReference);
      clear();
    }
  };
  if (!loaded) return <Loading />;
  if (!page)
    return (
      <main>
        <Alert kind="error">
          {ar ? 'رابط التوقيع غير صالح أو انتهت صلاحيته.' : 'The signing link is invalid or has expired.'}
        </Alert>
      </main>
    );
  if (reference || page.alreadySigned)
    return (
      <main>
        <Alert kind="success">
          {ar
            ? 'تم توقيع العقد. يُجهز الملف الموقع بشكل منفصل.'
            : 'The contract is signed. Its PDF is processed separately.'}
        </Alert>
        <p dir="ltr">{reference}</p>
      </main>
    );
  return (
    <main>
      <h1>{ar ? 'مراجعة العقد والتوقيع' : 'Review and sign the contract'}</h1>
      <ContractDocument json={page.contentJson} />
      <p>
        {ar
          ? 'نحتفظ بوقت التوقيع وعنوان الإنترنت وبيانات المتصفح المتاحة كأدلة تقنية للتوقيع.'
          : 'We retain the signing time, IP address and available browser information as technical signing evidence.'}
      </p>
      <form className="er-form er-card" onSubmit={(e) => void submit(e)}>
        {action.error && <Alert kind="error">{action.error}</Alert>}
        <Field id="signer-name" label={ar ? 'اسم الموقع' : 'Signer name'}>
          <input
            id="signer-name"
            required
            maxLength={200}
            value={name}
            disabled={action.busy}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        {page.isCompany && (
          <Field id="signer-title" label={ar ? 'الصفة (اختياري)' : 'Role/title (optional)'}>
            <input id="signer-title" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
        )}
        <p id="signature-instructions">
          {ar
            ? 'ارسم توقيعك باللمس أو القلم أو الفأرة. يمكنك مسحه وإعادة رسمه.'
            : 'Draw your signature with touch, pen or mouse. You can clear and redraw it.'}
        </p>
        <canvas
          ref={canvas}
          width={700}
          height={220}
          className="er-signature"
          aria-label={ar ? 'التوقيع المرسوم' : 'Drawn signature'}
          aria-describedby="signature-instructions"
          onPointerDown={(e) => {
            if (!action.busy) {
              previous.current = point(e);
              e.currentTarget.setPointerCapture(e.pointerId);
            }
          }}
          onPointerMove={stroke}
          onPointerUp={() => {
            previous.current = null;
          }}
          onPointerCancel={() => {
            previous.current = null;
          }}
        />
        <button className="er-button secondary" type="button" disabled={action.busy} onClick={clear}>
          {ar ? 'مسح التوقيع' : 'Clear signature'}
        </button>
        <label className="er-check">
          <input
            type="checkbox"
            checked={accepted}
            disabled={action.busy}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span style={{ whiteSpace: 'pre-wrap' }}>{page.acceptanceText}</span>
        </label>
        {page.isCompany && (
          <label className="er-check">
            <input
              type="checkbox"
              checked={declared}
              disabled={action.busy}
              onChange={(e) => setDeclared(e.target.checked)}
            />
            <span style={{ whiteSpace: 'pre-wrap' }}>{page.companyDeclarationText}</span>
          </label>
        )}
        <button
          className="er-button"
          type="submit"
          disabled={action.busy || !name.trim() || !drawn || !accepted || (page.isCompany && !declared)}
        >
          {ar ? 'توقيع العقد نهائيًا' : 'Sign contract now'}
        </button>
      </form>
    </main>
  );
}
